import React, { useState } from 'react'
import {
    Box,
    Divider,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import Button from '../../components/Button/Button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from '../../helpers/validations/loginValidation';
import { adminResetExpiredPassword } from '../../api/login/loginService';
import { useSnackbar } from '../../components/MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import PeriodicOtpVerification from './PeriodicOtpVerification';
import { Link } from 'react-router-dom';


const PeriodicPasswordReset = ({ userName, registeredUserName, onCancel }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [hasTriedSendOtp, setHasTriedSendOtp] = useState(false);
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const handleTogglePassword = () => setShowPassword((prev) => !prev);
    const [position, setPosition] = React.useState(50);
    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);
    const {
        register,
        formState: { errors, isSubmitting },
        getValues,
        watch,
    } = useForm({
        resolver: yupResolver(loginSchema),
    });

    const watchedPassword = watch("Password") || "";
    const watchedConfirmPassword = watch("ConfirmPassword") || "";
    const isPasswordRuleMet = watchedPassword.length >= 8 && /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&^()_+=-]).*$/.test(watchedPassword);
    const isPasswordGuidelineMet =
        isPasswordRuleMet && watchedPassword === watchedConfirmPassword;

    const showPasswordError = hasTriedSendOtp && !isPasswordRuleMet;
    const showConfirmPasswordError =
        hasTriedSendOtp && watchedConfirmPassword !== watchedPassword;
    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (isSendingOtp) return;
        setHasTriedSendOtp(true);
        const password = getValues('Password');
        const confirmPassword = getValues('ConfirmPassword');
        if (password !== confirmPassword) {
            showSnackbar('Password and Confirm Password must match.', 'error');
            return;
        }
        if (!isPasswordGuidelineMet) {
            showSnackbar('Password does not meet the required guidelines.', 'error');
            return;
        }
        try {
            setIsSendingOtp(true);
            const result = await adminResetExpiredPassword({ userName });
            if (result?.success === true && result?.data?.success === true) {
                setNewPassword(password);
                setIsOtpSent(true);
                showSnackbar(result?.data?.message, 'success');
                return;
            }
            showSnackbar(result?.data?.message || result?.message, 'error');
        } catch (err) {
            showSnackbar(err.message, 'error');
        } finally {
            setIsSendingOtp(false);
        }
    };

    if (isOtpSent) {
        return <PeriodicOtpVerification userName={userName} registeredUserName={registeredUserName} password={newPassword} onCancel={onCancel} />;
    }

    return (
        <Box minHeight="350px" gap={1}>
            <Box mb={1}>
                <Typography
                    variant="h6"
                    mb={{ xs: 1, sm: 1, md: 2, lg: 1, xl: 1.25 }}
                    color="#1d293d"
                    className="card-title text-center"
                    fontSize={{ xs: 20, sm: 20, md: 26, lg: 22, xl: 26 }}
                >
                    Please Reset Your Password
                </Typography>
                <Typography variant="body2" color="rgba(0,0,0,0.6)">
                    Your password is expired, create new one
                </Typography>
            </Box>
            <Typography
                variant="caption"
                sx={{
                    color: theme.palette.background.bgred,
                    fontSize: '12px',
                }}
            >
                {/* &bull; <strong>Password</strong> is required and cannot be left blank. */}
                &bull; Add numbers and special characters.
                <br />&bull; Must be at least <strong>8 characters</strong> long.
                <br />&bull; Avoid common passwords.
                {/* <br />&bull; Must not exceed <strong>12 characters</strong>. */}
                <br />&bull; Include <strong>uppercase and lowercase</strong> letters.
                <br />&bull; <strong>Password</strong> and <strong>Confirm Password</strong> must match.

            </Typography>
            <Box mt={2}>
                <form noValidate onSubmit={(e) => handleSendOtp(e)}>
                    <Box
                        sx={{
                            mx: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, sm: 2, md: 2, lg: 1, xl: 2 },
                            height: '100%',
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            className={`autofill_inputlog ${isDark ? 'darkmd' : 'lightmd'}`}
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            InputLabelProps={{
                                sx: {
                                    color: '#2c2c2c',
                                },
                            }}
                            inputProps={{
                                style: {
                                    color: '#2c2c2c',
                                },
                                placeholder: 'Enter your password',
                            }}
                            sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    '& fieldset': {
                                        borderColor: '#e2e8f0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#e2e8f0',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#e2e8f0',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#2c2c2c',
                                    '&.Mui-focused': {
                                        color: '#1976d2',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    color: '#334155',
                                },
                                '& .MuiOutlinedInput-root.Mui-error': {
                                    '& fieldset, &:hover fieldset, &.Mui-focused fieldset': {
                                        borderColor: theme.palette.background.bgred,
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-error': {
                                    color: theme.palette.background.bgred,
                                    '&.Mui-focused': {
                                        color: theme.palette.background.bgred,
                                    },
                                },
                            }}
                            error={!!errors.Password || showPasswordError}
                            {...register('Password')}
                            onInput={(e) => {
                                e.target.value = e.target.value.trim();
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleTogglePassword}
                                            sx={{
                                                backgroundColor: 'transparent',
                                                color: '#2c2c2c',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }}
                                        >
                                            {showPassword ? <i class="fa-sharp fa-regular fa-eye-slash" style={{ fontSize: "16px" }}></i> : <i class="fa-sharp fa-regular fa-eye" style={{ fontSize: "16px" }}></i>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            className={`autofill_inputlog ${isDark ? 'darkmd' : 'lightmd'}`}
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            InputLabelProps={{
                                sx: {
                                    color: '#2c2c2c',
                                },
                            }}
                            inputProps={{
                                style: {
                                    color: '#2c2c2c',
                                },
                                placeholder: 'Confirm your password',
                            }}
                            sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    '& fieldset': {
                                        borderColor: '#e2e8f0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#e2e8f0',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#e2e8f0',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#2c2c2c',
                                    '&.Mui-focused': {
                                        color: '#1976d2',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    color: '#334155',
                                },
                                '& .MuiOutlinedInput-root.Mui-error': {
                                    '& fieldset, &:hover fieldset, &.Mui-focused fieldset': {
                                        borderColor: theme.palette.background.bgred,
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-error': {
                                    color: theme.palette.background.bgred,
                                    '&.Mui-focused': {
                                        color: theme.palette.background.bgred,
                                    },
                                },
                            }}
                            error={!!errors.ConfirmPassword || showConfirmPasswordError}
                            {...register('ConfirmPassword')}
                            onInput={(e) => {
                                e.target.value = e.target.value.trim();
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleToggleConfirmPassword}
                                            sx={{
                                                backgroundColor: 'transparent',
                                                color: '#2c2c2c',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }}
                                        >
                                            {showConfirmPassword ? <i class="fa-sharp fa-regular fa-eye-slash" style={{ fontSize: "16px" }}></i> : <i class="fa-sharp fa-regular fa-eye" style={{ fontSize: "16px" }}></i>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box className="modal-btn-voilet">
                            <Button
                                variant="contained"
                                fullWidth
                                bgColor={theme.palette.background.bgpurple}
                                textColor={theme.palette.custom.whitetext}
                                padding="12px 10px"
                                fontSize="16px"
                                disabled={isSubmitting || isSendingOtp}
                                type="submit"
                                sx={{ width: '100%', fontWeight: 600 }}
                            >
                                {(isSubmitting || isSendingOtp) ? (
                                    <div class="loaderbtn"></div>
                                ) : (
                                    "Send OTP"
                                )}
                            </Button>
                        </Box>

                        <Divider
                            sx={{
                                mt: { xs: 2, sm: 2, md: 2, lg: 1, xl: 2 },
                                borderColor: '#adadad !important',
                                '& .MuiDivider-wrapper': {
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#adadad',
                                },
                                '--Divider-childPosition': `${position}%`,
                            }}
                        ></Divider>

                        <Box
                            pt={1}
                            textAlign={'center'}
                            color={'#adadad'}
                            fontSize={13}
                            fontWeight={400}
                        >
                            <Link sx={{ cursor: 'pointer' }} style={{ color: '#adadad' }} to="/privacy-notice" target="_blank"
                                rel="noopener noreferrer">
                                Privacy Notice
                            </Link>{' '}
                            |{' '}
                            <Link sx={{ cursor: 'pointer' }} style={{ color: '#adadad' }} to="/terms" target="_blank"
                                rel="noopener noreferrer">
                                Term of service
                            </Link>
                        </Box>

                    </Box>
                </form>
            </Box>
        </Box>
    )
}

export default PeriodicPasswordReset;
